import React, { useContext, useEffect, useState } from 'react';

import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact, { BugsnagErrorBoundary } from '@bugsnag/plugin-react';

import config from '@/appConfig';
import LoadingSpinner from '@/components/LoadingSpinner';
import { AuthContext } from '@/core/auth/AuthContext';


const { apiKey, stage, version } = config().bugsnag;

let BugsnagBoundary: BugsnagErrorBoundary | undefined;

type Props = {
  children: React.ReactElement;
};

/**
 * BugsnagWrapper initializes the Bugsnag client if BUGSNAG_API_KEY is present.
 * On initialization, a loading spinner is rendered. If initialized, children are
 * wrapped in an error boundary that reports errors to Bugsnag. Otherwise, children
 * are simply rendered.
 */
const BugsnagWrapper = (props: Props) => {
  const { children } = props;
  const { currentUser } = useContext(AuthContext);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (apiKey) {
      Bugsnag.start({
        apiKey,
        appType: 'client',
        appVersion: version,
        plugins: [new BugsnagPluginReact()],
        releaseStage: stage,
        user: {
          id: `${currentUser?.id}`,
        },
      });
      BugsnagBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(React);
    }

    setInitialized(true);
  }, []);

  if (!initialized) {
    return (
      <LoadingSpinner />
    );
  }

  if (BugsnagBoundary) {
    return (
      <BugsnagBoundary>
        {children}
      </BugsnagBoundary>
    );
  }

  return children;
};

export default BugsnagWrapper;
